const SAO_PAULO_TZ = 'America/Sao_Paulo';

export const PERIOD_PRESET_OPTIONS = [
  { value: 'ALL', label: 'Todo o período' },
  { value: 'LAST_7_DAYS', label: 'Últimos 7 dias' },
  { value: 'LAST_30_DAYS', label: 'Últimos 30 dias' },
  { value: 'THIS_MONTH', label: 'Este mês' },
  { value: 'LAST_MONTH', label: 'Mês passado' },
  { value: 'THIS_QUARTER', label: 'Este trimestre' },
  { value: 'LAST_QUARTER', label: 'Trimestre passado' },
  { value: 'THIS_YEAR', label: 'Este ano' },
];

export const DEFAULT_PERIOD_PRESET = 'LAST_30_DAYS';

const spWallDateParts = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: SAO_PAULO_TZ,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(date);
  return {
    y: Number(parts.find((p) => p.type === 'year')?.value),
    m: Number(parts.find((p) => p.type === 'month')?.value),
    d: Number(parts.find((p) => p.type === 'day')?.value),
  };
};

const toIsoDate = (y, m, d) =>
  `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

const lastDayOfMonth = (y, m) => new Date(y, m, 0).getDate();

const addDays = (y, m, d, delta) => {
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  return { y: dt.getFullYear(), m: dt.getMonth() + 1, d: dt.getDate() };
};

const quarterStartMonth = (month1to12) => Math.floor((month1to12 - 1) / 3) * 3 + 1;

export const getPeriodPresetLabel = (preset) =>
  PERIOD_PRESET_OPTIONS.find((o) => o.value === preset)?.label ?? 'Período';

/**
 * Resolve preset em intervalo [from, to] (YYYY-MM-DD) no calendário de São Paulo.
 * Retorna { from, to, label } — from/to null quando ALL.
 */
export const resolvePeriodRange = (preset, refDate = new Date()) => {
  const option = PERIOD_PRESET_OPTIONS.find((o) => o.value === preset);
  if (!preset || preset === 'ALL') {
    return { from: null, to: null, label: option?.label ?? 'Todo o período' };
  }

  const { y, m, d } = spWallDateParts(refDate);
  const today = toIsoDate(y, m, d);

  switch (preset) {
    case 'LAST_7_DAYS': {
      const start = addDays(y, m, d, -6);
      return {
        from: toIsoDate(start.y, start.m, start.d),
        to: today,
        label: option.label,
      };
    }
    case 'LAST_30_DAYS': {
      const start = addDays(y, m, d, -29);
      return {
        from: toIsoDate(start.y, start.m, start.d),
        to: today,
        label: option.label,
      };
    }
    case 'THIS_MONTH':
      return {
        from: toIsoDate(y, m, 1),
        to: today,
        label: option.label,
      };
    case 'LAST_MONTH': {
      let ly = y;
      let lm = m - 1;
      if (lm < 1) {
        lm = 12;
        ly -= 1;
      }
      return {
        from: toIsoDate(ly, lm, 1),
        to: toIsoDate(ly, lm, lastDayOfMonth(ly, lm)),
        label: option.label,
      };
    }
    case 'THIS_QUARTER': {
      const qm = quarterStartMonth(m);
      return {
        from: toIsoDate(y, qm, 1),
        to: today,
        label: option.label,
      };
    }
    case 'LAST_QUARTER': {
      let qy = y;
      let qm = quarterStartMonth(m) - 3;
      if (qm < 1) {
        qm += 12;
        qy -= 1;
      }
      const endM = qm + 2;
      return {
        from: toIsoDate(qy, qm, 1),
        to: toIsoDate(qy, endM, lastDayOfMonth(qy, endM)),
        label: option.label,
      };
    }
    case 'THIS_YEAR':
      return {
        from: toIsoDate(y, 1, 1),
        to: today,
        label: option.label,
      };
    default:
      return { from: null, to: null, label: 'Todo o período' };
  }
};

export const buildPeriodApiParams = (preset) => {
  const { from, to, label } = resolvePeriodRange(preset);
  return {
    from: from || undefined,
    to: to || undefined,
    label,
  };
};
